import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Circle,
  DatabaseZap,
  FileText,
  Hash,
  Loader2,
  Play,
  RefreshCw,
  Rocket,
  X,
  XCircle,
} from 'lucide-react';
import '@fontsource-variable/geist/wght.css';
import '@fontsource/geist-mono/500.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { useCxExpToast } from '../../customer-experience/components/CxExpSonnerToast';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import '../../users-customers/components/CustomersOauthConfig.css';
import './PainelControleOauthConfig.css';
import './PainelControleDbVervifyConfig.css';
import PainelControleDbVervifyCutover from './PainelControleDbVervifyCutover';
import PainelControleDbVervifyCounters from './PainelControleDbVervifyCounters';

const API_BASE_URL = getApiBaseUrl();
const POLL_MS = 1200;

async function api(path, options = {}) {
  const authToken = localStorage.getItem('authToken');
  const response = await fetch(`${API_BASE_URL}/api/config/db-vervify${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || result.success === false) {
    throw new Error(result.message || 'Falha na comunicação com o servidor');
  }
  return result;
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

function formatNumber(n) {
  return Number(n || 0).toLocaleString('pt-BR');
}

function durationLabel(start, end) {
  if (!start || !end) return '';
  const ms = new Date(end) - new Date(start);
  if (!(ms >= 0)) return '';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} s`;
}

function Pcs2Section({ icon: Icon, title, desc, children }) {
  return (
    <section className="pcs2-section customers-oauth-section">
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} strokeWidth={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2SettingRow({ title, desc, children }) {
  return (
    <div className="pcs2-setting-row">
      <div className="pcs2-setting-copy">
        <span className="pcs2-setting-title">{title}</span>
        {desc ? <span className="pcs2-setting-desc">{desc}</span> : null}
      </div>
      {children ? <div className="pcs2-setting-actions">{children}</div> : null}
    </div>
  );
}

function StepIcon({ status }) {
  if (status === 'done') return <CheckCircle2 size={15} strokeWidth={2} className="pc-dbv-step-ico is-done" aria-hidden />;
  if (status === 'failed') return <XCircle size={15} strokeWidth={2} className="pc-dbv-step-ico is-failed" aria-hidden />;
  if (status === 'running') return <Loader2 size={15} strokeWidth={2} className="pc-dbv-step-ico is-running" aria-hidden />;
  return <Circle size={15} strokeWidth={1.75} className="pc-dbv-step-ico" aria-hidden />;
}

function StepList({ steps }) {
  return (
    <ol className="pc-dbv-steps">
      {steps.map((s) => (
        <li key={s.key} className={`pc-dbv-step is-${s.status}`}>
          <StepIcon status={s.status} />
          <span className="pc-dbv-step-label">{s.label}</span>
          <span className="pc-dbv-step-count">
            {s.status === 'pending' ? '' : `${Math.min(s.done, s.total)}/${s.total}`}
          </span>
          {s.error ? <span className="pc-dbv-step-error">{s.error}</span> : null}
        </li>
      ))}
    </ol>
  );
}

function ProgressBar({ value, failed }) {
  return (
    <div
      className="pc-dbv-track"
      role="progressbar"
      aria-label="Progresso da migração"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
    >
      <div className={`pc-dbv-fill${failed ? ' is-failed' : ''}`} style={{ width: `${value}%` }} />
    </div>
  );
}

function SummaryChips({ summary }) {
  if (!summary) return null;
  const validation =
    summary.validationOk == null ? '—' : summary.validationOk ? 'OK' : 'Divergências';
  return (
    <div className="pc-dbv-chips">
      <span className="pc-dbv-chip"><b>{formatNumber(summary.tables)}</b> tabelas</span>
      <span className="pc-dbv-chip"><b>{formatNumber(summary.rowsSource)}</b> registros</span>
      <span className="pc-dbv-chip"><b>{formatNumber(summary.warnings)}</b> avisos</span>
      <span className={`pc-dbv-chip${summary.validationOk === false ? ' is-warn' : ''}`}>
        Validação <b>{validation}</b>
      </span>
    </div>
  );
}

function ReportModal({ report, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const step = (key) => report.steps?.find((s) => s.key === key);
  const migrations = step('migrations');
  const loads = ['processes', 'team', 'customers', 'integrations'].map(step).filter(Boolean);
  const validation = step('validation');
  const warnings = loads.flatMap((s) => s.summary?.warnings || []);

  return createPortal(
    <div className="pc-dbv-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pc-dbv-modal" role="dialog" aria-modal="true" aria-label="Relatório da migração">
        <header className="pc-dbv-modal-head">
          <div>
            <h3 className="pc-dbv-modal-title">
              {report.kind === 'update' ? 'Relatório da atualização' : 'Relatório da migração'}
            </h3>
            <p className="pc-dbv-modal-sub">
              {report.status === 'success' ? 'Concluída' : 'Falhou'} em {formatDateTime(report.finishedAt)}
              {report.startedBy ? ` · por ${report.startedBy}` : ''}
              {` · ${durationLabel(report.startedAt, report.finishedAt)}`}
            </p>
          </div>
          <button type="button" className="pc-dbv-icon-btn" onClick={onClose} aria-label="Fechar">
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className="pc-dbv-modal-body">
          {report.error ? (
            <p className="pc-dbv-alert is-error" role="alert">
              <XCircle size={15} strokeWidth={2} aria-hidden /> {report.error}
            </p>
          ) : null}

          <SummaryChips summary={report.summary} />

          <h4 className="pc-dbv-h4">Etapas</h4>
          <StepList steps={report.steps || []} />

          {migrations?.items?.length ? (
            <>
              <h4 className="pc-dbv-h4">Estrutura (migrations)</h4>
              <table className="pc-dbv-table">
                <thead>
                  <tr><th>Versão</th><th>Situação</th></tr>
                </thead>
                <tbody>
                  {migrations.items.map((i) => (
                    <tr key={i.version}>
                      <td className="is-mono">{i.version}</td>
                      <td>{i.status === 'applied' ? 'Aplicada' : 'Já existente'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}

          {loads.map((s) =>
            s.items?.length ? (
              <React.Fragment key={s.key}>
                <h4 className="pc-dbv-h4">{s.label}</h4>
                <table className="pc-dbv-table">
                  <thead>
                    <tr><th>Tabela</th><th className="is-num">Origem</th><th className="is-num">Gravados</th><th className="is-num">Removidos</th></tr>
                  </thead>
                  <tbody>
                    {s.items.map((i) => (
                      <tr key={i.table}>
                        <td className="is-mono">{i.table}</td>
                        <td className="is-num">{formatNumber(i.rows)}</td>
                        <td className="is-num">{formatNumber(i.upserted)}</td>
                        <td className="is-num">{formatNumber(i.deleted)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </React.Fragment>
            ) : null
          )}

          {validation?.items?.length ? (
            <>
              <h4 className="pc-dbv-h4">{validation.label}</h4>
              <table className="pc-dbv-table">
                <thead>
                  <tr><th>Tabela</th><th className="is-num">SQL Server</th><th className="is-num">Vervify</th><th>Resultado</th></tr>
                </thead>
                <tbody>
                  {validation.items.map((i) => (
                    <tr key={`${i.module}:${i.table}`}>
                      <td className="is-mono">{i.table}</td>
                      <td className="is-num">{formatNumber(i.expected)}</td>
                      <td className="is-num">{formatNumber(i.actual)}</td>
                      <td>
                        {i.ok ? (
                          <span className="pc-dbv-ok">OK</span>
                        ) : (
                          <span className="pc-dbv-diff">
                            {[
                              i.missing ? `${i.missing} faltando` : '',
                              i.extra ? `${i.extra} a mais` : '',
                              i.changed ? `${i.changed} diferentes` : '',
                            ].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : null}

          {warnings.length ? (
            <>
              <h4 className="pc-dbv-h4">Avisos de dados ({warnings.length})</h4>
              <p className="pc-dbv-hint">
                Referências existentes no SQL Server sem correspondência válida. Os registros foram migrados com o vínculo em branco.
              </p>
              <ul className="pc-dbv-warnings">
                {warnings.map((w) => (
                  <li key={w}><AlertTriangle size={13} strokeWidth={2} aria-hidden /> {w}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function migrationDescription(job, lastRun, pendingCount) {
  if (job?.status === 'running') {
    return job.kind === 'update'
      ? 'Atualização em andamento. Acompanhe as etapas abaixo.'
      : 'Migração em andamento. Acompanhe as etapas abaixo.';
  }
  if (pendingCount > 0 && (lastRun?.hasSuccess || job?.status === 'success')) {
    return pendingCount === 1
      ? 'Há 1 atualização de estrutura pendente. Aplique para criar as novas tabelas e recarregar os dados.'
      : `Há ${pendingCount} atualizações de estrutura pendentes. Aplique para criar as novas tabelas e recarregar os dados.`;
  }
  const run = job && job.status !== 'running'
    ? { status: job.status, finishedAt: job.finishedAt, startedBy: job.startedBy }
    : lastRun;
  if (run) {
    const who = run.startedBy ? ` por ${run.startedBy}` : '';
    return run.status === 'success'
      ? `Executada em ${formatDateTime(run.finishedAt)}${who}.`
      : `Última execução falhou em ${formatDateTime(run.finishedAt)}${who}.`;
  }
  return 'Cria a estrutura no banco Vervify, copia os cadastros de Processos, Gestão de equipe e Customers e valida o resultado.';
}

export default function PainelControleDbVervifyConfig() {
  const { success: toastSuccess, error: toastError, Toaster } = useCxExpToast();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [status, setStatus] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [cutoverReloadKey, setCutoverReloadKey] = useState(0);

  const loadStatus = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const result = await api('');
      setStatus(result);
      setLoadError('');
      return result;
    } catch (e) {
      if (!silent) setLoadError(e.message);
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const job = status?.job || null;
  const running = job?.status === 'running';

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => loadStatus({ silent: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [running, loadStatus]);

  const startMigration = async () => {
    setStarting(true);
    try {
      const result = await api('/provision', { method: 'POST' });
      setStatus((s) => ({ ...s, job: result.job }));
    } catch (e) {
      toastError(e.message);
    } finally {
      setStarting(false);
      setConfirming(false);
    }
  };

  const openReport = async () => {
    if (job && job.status !== 'running' && !job.runId) {
      setReport(job);
      return;
    }
    setLoadingReport(true);
    try {
      const result = await api(job?.runId ? `/report?id=${job.runId}` : '/report');
      setReport(result.report);
    } catch (e) {
      toastError(e.message);
    } finally {
      setLoadingReport(false);
    }
  };

  const lastRun = status?.lastRun || null;
  const alreadyMigrated = Boolean(lastRun?.hasSuccess) || job?.status === 'success';
  const migrationDate = alreadyMigrated ? status?.startDate || '' : '';
  const hasReport = Boolean(lastRun) || Boolean(job && !running);
  const pendingCount = status?.pendingMigrations?.length || 0;
  const hasUpdates = alreadyMigrated && pendingCount > 0 && !running;

  const runLabel = running
    ? 'Em execução'
    : hasUpdates
      ? 'Aplicar atualizações'
      : alreadyMigrated
        ? 'Migração concluída'
        : lastRun || job
          ? 'Executar novamente'
          : 'Executar migração';
  const jobDoneLabel = job?.kind === 'update' ? 'Atualização concluída' : 'Migração concluída';
  const jobFailedLabel = job?.kind === 'update' ? 'Atualização falhou' : 'Migração falhou';

  return (
    <div className="pcs2-page customers-oauth-page painel-controle-oauth-page pc-dbv-page">
      <Pcs2Section
        icon={CalendarClock}
        title="Data da migração"
        desc="Consulta de quando a migração para o banco Vervify foi concluída."
      >
        {loading ? (
          <ProcessConfigSkeleton variant="settings" embedded ariaLabel="Carregando data inicial" />
        ) : loadError ? (
          <div className="customers-oauth-error">
            <span>{loadError}</span>
            <button type="button" className="customers-oauth-retry" onClick={() => loadStatus()}>
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
            <Pcs2SettingRow
              title="Data da migração"
              desc="Registrada automaticamente quando a migração termina com sucesso. Serve só para consulta."
            >
              <span className={`pc-dbv-date-value${migrationDate ? '' : ' is-empty'}`}>
                {migrationDate ? formatDate(migrationDate) : alreadyMigrated ? 'Não registrada' : 'Aguardando a migração'}
              </span>
            </Pcs2SettingRow>
          </div>
        )}
      </Pcs2Section>

      <hr className="pcs2-divider" />

      <Pcs2Section
        icon={DatabaseZap}
        title="Migração de dados"
        desc="Estrutura e carga inicial dos cadastros no banco Vervify. Execução única; novas atualizações de estrutura são liberadas quando houver pendência."
      >
        {loading ? (
          <ProcessConfigSkeleton variant="settings" embedded ariaLabel="Carregando migração" />
        ) : loadError ? null : (
          <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
            <Pcs2SettingRow title="Migração inicial" desc={migrationDescription(job, lastRun, pendingCount)}>
              {confirming ? (
                <>
                  <button type="button" className="pc-dbv-btn" onClick={() => setConfirming(false)} disabled={starting}>
                    Cancelar
                  </button>
                  <button type="button" className="pc-dbv-btn is-primary" onClick={startMigration} disabled={starting}>
                    {starting ? <Loader2 size={14} strokeWidth={2} className="pcs2-spin" aria-hidden /> : null}
                    Confirmar
                  </button>
                </>
              ) : (
                <>
                  {hasReport ? (
                    <button type="button" className="pc-dbv-btn" onClick={openReport} disabled={loadingReport}>
                      {loadingReport ? (
                        <Loader2 size={14} strokeWidth={2} className="pcs2-spin" aria-hidden />
                      ) : (
                        <FileText size={14} strokeWidth={1.75} aria-hidden />
                      )}
                      Ver relatório
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="pc-dbv-btn is-primary"
                    onClick={() => setConfirming(true)}
                    disabled={(alreadyMigrated && !hasUpdates) || running}
                    title={
                      hasUpdates
                        ? `${pendingCount} atualização(ões) pendente(s): ${status.pendingMigrations.join(', ')}`
                        : alreadyMigrated
                          ? 'A migração já foi executada e a estrutura está atualizada.'
                          : undefined
                    }
                  >
                    {running ? (
                      <Loader2 size={14} strokeWidth={2} className="pcs2-spin" aria-hidden />
                    ) : hasUpdates ? (
                      <RefreshCw size={14} strokeWidth={2} aria-hidden />
                    ) : alreadyMigrated ? (
                      <CheckCircle2 size={14} strokeWidth={2} aria-hidden />
                    ) : (
                      <Play size={14} strokeWidth={2} aria-hidden />
                    )}
                    {runLabel}
                  </button>
                </>
              )}
            </Pcs2SettingRow>

            {confirming ? (
              <p className="pc-dbv-confirm-text">
                {hasUpdates
                  ? 'Aplica as atualizações de estrutura pendentes, recarrega os dados e valida o resultado. Deseja continuar?'
                  : 'Esta ação só pode ser executada uma vez. Deseja continuar?'}
              </p>
            ) : null}

            {job ? (
              <div className="pc-dbv-run">
                <div className="pc-dbv-run-head">
                  <span className="pc-dbv-run-title">
                    {running
                      ? job.kind === 'update' ? 'Atualizando…' : 'Migrando dados…'
                      : job.status === 'success' ? jobDoneLabel : jobFailedLabel}
                  </span>
                  <span className="pc-dbv-run-pct">{job.progress}%</span>
                </div>
                <ProgressBar value={job.progress} failed={job.status === 'failed'} />
                <StepList steps={job.steps} />
                {!running ? <SummaryChips summary={job.summary} /> : null}
              </div>
            ) : null}
          </div>
        )}
      </Pcs2Section>

      {!loading && !loadError ? (
        <>
          <hr className="pcs2-divider" />
          <Pcs2Section
            icon={Hash}
            title="Contadores"
            desc="Numeração das OS abertas no banco Vervify após a ativação da operação nativa."
          >
            <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
              <PainelControleDbVervifyCounters
                toastSuccess={toastSuccess}
                toastError={toastError}
                reloadKey={job?.finishedAt || ''}
                onSaved={() => setCutoverReloadKey((k) => k + 1)}
              />
            </div>
          </Pcs2Section>

          <hr className="pcs2-divider" />
          <Pcs2Section
            icon={Rocket}
            title="Operação nativa"
            desc="Passo final da migração: técnicos e OS novas passam a ser do banco Vervify. Acompanhe aqui as OS do ERP até a operação ficar 100% Postgres."
          >
            <div className="pcs2-card pcs2-card--flat pcs2-card--spaced">
              <PainelControleDbVervifyCutover
                toastSuccess={toastSuccess}
                toastError={toastError}
                reloadKey={cutoverReloadKey}
              />
            </div>
          </Pcs2Section>
        </>
      ) : null}

      {report ? <ReportModal report={report} onClose={() => setReport(null)} /> : null}

      {Toaster}
    </div>
  );
}
